import { Inject } from 'vue-di-container';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Toaster } from '../../../Services/toast';
import ValidationService from '../../../Services/ValidationService';
import { PreApprovedTemplateModel } from '../PreApprovedTemplateModel';
import PreApprovedTemplateService from '../PreApprovedTemplateService';
import WithRender from './AddPreApprovedTemplate.html';
import { DropdownModel } from '../../Provider/ProviderModel';
import { isolateTemplateVariablePlaceholders } from '../../../shared/templateTextDirection';
import { TemplateVariableValidation } from '../../../shared/templateVariableValidation';

@WithRender
@Component({
    components: {},
    diProvide: [
        PreApprovedTemplateService,
    ],
})
export default class AddPreApprovedTemplate extends Vue {
    @Inject(PreApprovedTemplateService) public preApprovedTemplateService!: PreApprovedTemplateService;
    @Inject(ValidationService) public validationService!: ValidationService;

    @Prop({ type: String, default: 'Add' })
    public mode: string | any;

    @Prop({ default: '' })
    public id!: string;

    @Prop({ type: Boolean, default: false })
    public layoutSup: boolean | any;

    public confirmDialog: boolean = false;
    public classificationList: DropdownModel[] = [];
    public templateVariableExamples: string[] = [];
    public templateVariableMaxLengths: number[] = [];
    public templateVariableTypes: string[] = [];
    public readonly validVariableTypes: string[] = ['Numbers', 'Letters', 'Letters & Numbers', 'Link'];
    public templateTextDir: string = (typeof localStorage !== 'undefined' && localStorage.dir === 'rtl') ? 'rtl' : 'ltr';
    private lastValidatedTemplateContent: string = '';
    private isTemplateContentMutationAllowed: boolean = false;

    public setTemplateTextDir(dir: string): void {
        this.templateTextDir = dir === 'rtl' ? 'rtl' : 'ltr';
    }

    public get hasTemplateVariables(): boolean {
        return /#Var\d+#/i.test(this.model.templateContent || '');
    }

    public get substitutedTemplateContent(): string {
        const content = this.model.templateContent || '';
        return content.replace(/#Var(\d+)#/gi, (match: string, num: string) => {
            const index = parseInt(num, 10) - 1;
            const example = this.templateVariableExamples[index];
            if (example !== undefined && example !== null && example !== '') {
                return example;
            }
            return match;
        });
    }

    public formatTemplateContentForDisplay(content: string): string {
        return isolateTemplateVariablePlaceholders(content || '');
    }

    public model: PreApprovedTemplateModel = {
        id: '',
        name: '',
        description: '',
        templateContent: '',
        classification: 0,
        createdDate: '',
    };

    private getDefaultVariableExampleValue(): string {
        return '';
    }

    public created() {
        this.classificationList = [
            { id: 1, name: this.$t('shared.Advertising').toString() },
            { id: 2, name: this.$t('shared.Service').toString() },
            { id: 3, name: this.$t('shared.Warning').toString() },
            { id: 5, name: this.$t('shared.Awareness').toString() },
        ];
    }

    private setTemplateContentWithMutation(content: string): void {
        this.isTemplateContentMutationAllowed = true;
        this.model.templateContent = content;
        this.lastValidatedTemplateContent = content || '';
        this.$nextTick(() => {
            this.isTemplateContentMutationAllowed = false;
        });
    }



    private extractVariablePlaceholders(content: string): string[] {
        return (content || '').match(/#Var\d+#/gi) || [];
    }

    public addVariable() {
        if (this.areVariableActionsDisabled) {
            return;
        }
        if (this.templateVariableExamples.length >= TemplateVariableValidation.maxVariableCount) {
            Toaster.error(this.$t('shared.maxVariableCountExceeded', {
                count: TemplateVariableValidation.maxVariableCount,
            }).toString());
            return;
        }
        const content = this.model.templateContent || '';
        if (!(content || '').trim()) {
            Toaster.error(this.$t('shared.templateMustNotStartOrEndWithVariable').toString());
            return;
        }
        if (/#Var\d+#\s*$/.test(content)) {
            Toaster.error(this.$t('shared.adjacentVariablesNotAllowed').toString());
            return;
        }
        const nextIndex = this.templateVariableExamples.length + 1;
        const updatedContent = `${content}#Var${nextIndex}#`;

        this.setTemplateContentWithMutation(updatedContent);
        this.ensureVariableExampleCount(nextIndex);
        this.ensureVariableMaxLengthCount(nextIndex);
        this.ensureVariableTypeCount(nextIndex);

        this.$set(
            this.templateVariableExamples,
            nextIndex - 1,
            this.getDefaultVariableExampleValue(),
        );
        this.$set(this.templateVariableMaxLengths, nextIndex - 1, 0);
        this.$set(this.templateVariableTypes, nextIndex - 1, '');
    }

    public deleteVariable(index: number) {
        this.templateVariableExamples.splice(index - 1, 1);
        this.templateVariableMaxLengths.splice(index - 1, 1);
        this.templateVariableTypes.splice(index - 1, 1);

        let content = this.model.templateContent || '';
        content = content.replace(new RegExp(`#Var${index}#`, 'gi'), '');

        let currentMax = this.templateVariableExamples.length + 1;
        for (let i = index + 1; i <= currentMax; i++) {
            content = content.replace(new RegExp(`#Var${i}#`, 'gi'), `#Var${i - 1}#`);
        }

        this.setTemplateContentWithMutation(content);
    }

    public setVariableExample(index: number, value: string) {
        this.ensureVariableExampleCount(index);
        let truncated = value;
        const maxExampleLength = this.maxLengthInputMax;
        if (truncated.length > maxExampleLength) {
            truncated = truncated.substring(0, maxExampleLength);
        }
        this.$set(this.templateVariableExamples, index - 1, truncated);
    }

    public getVariableMaxLength(index: number): number | string {
        const value = this.templateVariableMaxLengths[index - 1];
        if (!value || value < 1) {
            return '';
        }
        return value;
    }

    public setVariableMaxLength(index: number, value: number) {
        this.ensureVariableMaxLengthCount(index);
        let clamped = Math.floor(value);
        if (isNaN(clamped) || clamped < 1) {
            this.$set(this.templateVariableMaxLengths, index - 1, 0);
            return;
        }
        if (clamped > this.maxLengthInputMax) {
            clamped = this.maxLengthInputMax;
        }
        this.$set(this.templateVariableMaxLengths, index - 1, clamped);
    }

    public onMaxLengthInput(index: number, event: Event) {
        const target = event.target as HTMLInputElement;
        if (!target || target.value === '') {
            this.setVariableMaxLength(index, 0);
            return;
        }
        const parsed = parseInt(target.value, 10);
        this.setVariableMaxLength(index, isNaN(parsed) ? 0 : parsed);
    }

    private ensureVariableMaxLengthCount(index: number) {
        while (this.templateVariableMaxLengths.length < index) {
            this.templateVariableMaxLengths.push(0);
        }
    }

    public getVariableType(index: number): string {
        return this.templateVariableTypes[index - 1] || '';
    }

    public setVariableType(index: number, value: string) {
        this.ensureVariableTypeCount(index);
        this.$set(this.templateVariableTypes, index - 1, value);
    }

    private ensureVariableTypeCount(index: number) {
        while (this.templateVariableTypes.length < index) {
            this.templateVariableTypes.push('');
        }
    }

    public get hasMissingVariableTypes(): boolean {
        return this.templateVariableExamples.length > 0
            && (this.templateVariableTypes.length !== this.templateVariableExamples.length
                || this.templateVariableTypes.some((t) => !t));
    }

    public get variableTypeOptions(): Array<{value: string; label: string}> {
        return [
            { value: 'Numbers', label: this.$t('shared.variableTypeNumbers').toString() },
            { value: 'Letters', label: this.$t('shared.variableTypeLetters').toString() },
            { value: 'Letters & Numbers', label: this.$t('shared.variableTypeLettersAndNumbers').toString() },
            { value: 'Link', label: this.$t('shared.variableTypeLink').toString() },
        ];
    }

    public get areVariableActionsDisabled(): boolean {
        return false;
    }

    public get maxLengthUpperBound(): number {
        return TemplateVariableValidation.maxLengthUpperBound;
    }

    public get maxLengthInputMax(): number {
        return TemplateVariableValidation.maxLengthUpperBound;
    }

    public get hasAdjacentVariables(): boolean {
        return /#Var\d+#\s*#Var\d+#/i.test(this.model.templateContent || '');
    }

    public get exceedsMaxVariableCount(): boolean {
        return this.templateVariableExamples.length > TemplateVariableValidation.maxVariableCount;
    }

    public get hasMaxLengthExceedingLimit(): boolean {
        return this.templateVariableMaxLengths.some((ml) => ml > TemplateVariableValidation.maxLengthUpperBound);
    }

    public get variableRatioPercent(): number {
        if (!this.templateVariableExamples.length) return 0;
        const totalMaxLength = TemplateVariableValidation.sumMaxLengths(this.templateVariableMaxLengths);
        const textContentLength = (this.model.templateContent || '').replace(/#Var\d+#/gi, '').length;
        return TemplateVariableValidation.getDisplayRatioPercent(totalMaxLength, textContentLength);
    }

    public get exceeds40Percent(): boolean {
        if (!this.templateVariableExamples.length) return false;
        const totalMaxLength = TemplateVariableValidation.sumMaxLengths(this.templateVariableMaxLengths);
        const textContentLength = (this.model.templateContent || '').replace(/#Var\d+#/gi, '').length;
        return TemplateVariableValidation.exceedsMaxRatio(totalMaxLength, textContentLength);
    }

    public get hasMissingMaxLength(): boolean {
        return this.templateVariableExamples.length > 0
            && (this.templateVariableMaxLengths.length !== this.templateVariableExamples.length
                || this.templateVariableMaxLengths.some((ml) => !ml || ml < 1));
    }

    public get hasEmptyVariableExamples(): boolean {
        return TemplateVariableValidation.hasEmptyExamples(this.templateVariableExamples);
    }

    public get hasZeroTextContentWithVariables(): boolean {
        return this.hasTemplateVariables && (this.model.templateContent || '').replace(/#Var\d+#/gi, '').length === 0;
    }

    public get variableValidationErrors(): string[] {
        const errors: string[] = [];
        if (this.hasAdjacentVariables) {
            errors.push(this.$t('shared.adjacentVariablesNotAllowed').toString());
        }
        if (this.exceedsMaxVariableCount) {
            errors.push(this.$t('shared.maxVariableCountExceeded', {
                count: TemplateVariableValidation.maxVariableCount,
            }).toString());
        }
        if (this.hasEmptyVariableExamples) {
            errors.push(this.$t('shared.variableExamplesRequired').toString());
        }
        if (this.hasMissingVariableTypes) {
            errors.push(this.$t('shared.variableTypeRequired').toString());
        }
        if (this.hasZeroTextContentWithVariables) {
            errors.push(this.$t('shared.textContentRequiredWhenVariablesPresent').toString());
        }
        if (this.hasMaxLengthExceedingLimit) {
            errors.push(this.$t('shared.maxLengthExceeded', {
                max: TemplateVariableValidation.maxLengthUpperBound,
            }).toString());
        }
        if (this.hasMissingMaxLength) {
            errors.push(this.$t('shared.maxLengthRequired', {
                max: TemplateVariableValidation.maxLengthUpperBound,
            }).toString());
        }
        if (this.exceeds40Percent) {
            errors.push(this.$t('shared.variablesMustNotExceed40Percent').toString());
        }
        return errors;
    }

    private ensureVariableExampleCount(index: number) {
        while (this.templateVariableExamples.length < index) {
            this.templateVariableExamples.push('');
        }
    }

    public get templateContentTotalCharacterCount(): number {
        return (this.model.templateContent || '').replace(/#Var\d+#/gi, '').length;
    }

    public get templateContentError(): string | null {
        if (!this.model.templateContent) return null;

        const trimmedContent = this.model.templateContent.trim();
        const startsWithVar = /^#Var\d+#/i.test(trimmedContent);
        const endsWithVar = /#Var\d+#$/i.test(trimmedContent);

        if (startsWithVar || endsWithVar) {
            return this.$t('shared.templateVariableBoundaryInvalid').toString();
        }
        return null;
    }

    public get templateDescriptionError(): string {
        const description = (this.model.description || '').trim();
        if (!description) return '';
        if (description.length < 3) {
            return this.$t('shared.templateDescriptionMinLength').toString();
        }
        if (description.length > 160) {
            return this.$t('shared.templateDescriptionMaxLength').toString();
        }
        return '';
    }

    @Watch('model.templateContent')
    public onTemplateContentChanged(newContent: string) {
        if (this.isTemplateContentMutationAllowed) {
            this.lastValidatedTemplateContent = newContent || '';
            return;
        }

        const previousContent = this.lastValidatedTemplateContent || '';
        const previousVariables = this.extractVariablePlaceholders(previousContent);
        const nextVariables = this.extractVariablePlaceholders(newContent || '');

        const variablesPreserved = previousVariables.length === nextVariables.length
            && previousVariables.every((token, index) => token.toLowerCase() === (nextVariables[index] || '').toLowerCase())
            && previousVariables.every((token) => (newContent || '').includes(token));

        if (!variablesPreserved) {
            this.isTemplateContentMutationAllowed = true;
            this.model.templateContent = previousContent;
            this.$nextTick(() => {
                this.isTemplateContentMutationAllowed = false;
            });
            return;
        }

        this.lastValidatedTemplateContent = newContent || '';
    }

    public async mounted() {
        this.$emit('childinit', this.layoutSup);

        this.lastValidatedTemplateContent = this.model.templateContent || '';
    }

    public get isValid(): boolean {
        const areVariablesValid = this.templateVariableExamples.every(
            value => value && value.trim() !== ''
        );

        const hasVariableErrors = this.hasTemplateVariables && this.variableValidationErrors.length > 0;

        return !!this.model.name
            && !!this.model.templateContent
            && !!this.model.classification
            && !this.templateDescriptionError
            && !this.templateContentError
            && areVariablesValid
            && !hasVariableErrors; 
    }

    public showConfirmDialog() {
        if (this.isValid) this.confirmDialog = true;
    }

    public hideConfirmDialog() {
        this.confirmDialog = false;
    }

    public async save() {
        const structuredPayload = {
            text: this.model.templateContent,
            variables: this.templateVariableExamples,
            maxLengths: this.hasTemplateVariables ? this.templateVariableMaxLengths : [],
            types: this.hasTemplateVariables ? this.templateVariableTypes : [],
        };

        const finalModel = {
            ...this.model,
            templateContent: JSON.stringify(structuredPayload)
        };

        const response = await this.preApprovedTemplateService.post(finalModel);

        if (response.isSuccess && response.result) {
            Toaster.success(
                this.$t('shared.addSuccessfully').toString());
            this.$router.push({ name: 'PreApprovedTemplates' });
        } else
        {
            Toaster.error(response.errorMessage);
        }
        this.confirmDialog = false;
    }
}